import type { IHardwareCapacitySnapshot } from '@lucimark/shared/models/playback-heartbeat'

/** Dev-only: force `mid` or `high` tier (emulator video testing). */
export type TDevHardwareCapacityTierOverride = 'mid' | 'high'

export const DEBUG_HARDWARE_CAPACITY_TIER_KEY = 'lucimark_debug_capacity_tier'

const VALID_OVERRIDE_TIERS = new Set<TDevHardwareCapacityTierOverride>(['mid', 'high'])

function parseOverrideTier(raw: string | null | undefined): TDevHardwareCapacityTierOverride | null {
  if (raw == null || raw === '') { return null }
  const normalized = raw.trim().toLowerCase()
  return VALID_OVERRIDE_TIERS.has(normalized as TDevHardwareCapacityTierOverride)
    ? normalized as TDevHardwareCapacityTierOverride
    : null
}

/**
 * Dev-only capacity tier override for emulators classified as `low`.
 *
 * Sources (first match wins):
 * 1. `VITE_DEV_HARDWARE_CAPACITY_TIER=mid|high` at build time
 * 2. URL `?lucimark_debug_capacity_tier=mid|high`
 * 3. `localStorage.lucimark_debug_capacity_tier=mid|high`
 *
 * Reload after changing URL/localStorage so registration bootstrap and asset index pick up the tier.
 */
export function getDevHardwareCapacityTierOverride(): TDevHardwareCapacityTierOverride | null {
  if (!import.meta.env.DEV) { return null }

  const fromEnv = parseOverrideTier(import.meta.env.VITE_DEV_HARDWARE_CAPACITY_TIER)
  if (fromEnv != null) { return fromEnv }

  if (typeof window !== 'undefined') {
    try {
      const fromQuery = parseOverrideTier(
        new URL(window.location.href).searchParams.get(DEBUG_HARDWARE_CAPACITY_TIER_KEY)
      )
      if (fromQuery != null) { return fromQuery }

      const fromStorage = parseOverrideTier(window.localStorage?.getItem(DEBUG_HARDWARE_CAPACITY_TIER_KEY))
      if (fromStorage != null) { return fromStorage }
    }
    catch {
      // ignore
    }
  }

  return null
}

export function applyDevHardwareCapacityTierOverride(
  cap: IHardwareCapacitySnapshot
): IHardwareCapacitySnapshot {
  const overrideTier = getDevHardwareCapacityTierOverride()
  if (overrideTier == null || cap.tier === overrideTier) { return cap }
  return { ...cap, tier: overrideTier }
}

export function isDevHardwareCapacityTierOverrideActive(): boolean {
  return getDevHardwareCapacityTierOverride() != null
}
